import { useQuery } from '@tanstack/react-query'
import { dashboardService } from '@services/dashboard.service'
import { clientService } from '@services/client.service'
import { inboxService } from '@services/inbox.service'
import type { Client } from '@/types'

export function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: dashboardService.getStats,
    refetchInterval: 30000, // Refresh every 30 seconds
  })
}

export function useRecentActivity(limit: number = 10) {
  return useQuery({
    queryKey: ['dashboard', 'activity', limit],
    queryFn: () => dashboardService.getRecentActivity(limit),
    refetchInterval: 60000, // Refresh every minute
  })
}

export function useEmailChartData(days: number = 7) {
  return useQuery({
    queryKey: ['dashboard', 'chart', days],
    queryFn: () => dashboardService.getEmailChartData(days),
    refetchInterval: 300000, // Refresh every 5 minutes
  })
}

export function usePerformanceMetrics() {
  return useQuery({
    queryKey: ['dashboard', 'performance'],
    queryFn: dashboardService.getPerformanceMetrics,
    refetchInterval: 60000, // Refresh every minute
  })
}

export interface TrackingSummary {
  responders: number
  nonResponders: number
  newActive: number
  inboxUnhandled: number
}

export function useTrackingSummary() {
  return useQuery({
    queryKey: ['dashboard', 'tracking-summary'],
    queryFn: async (): Promise<TrackingSummary> => {
      // Fetch clients and inbox stats in parallel
      const [clientsPage1, inboxStats] = await Promise.all([
        clientService.fetchClients({ limit: 100, page: 1 }),
        inboxService.getInboxStats(),
      ])

      // Fetch remaining pages if needed
      let allClients: Client[] = [...clientsPage1.data]
      const totalPages = clientsPage1.pagination.totalPages
      if (totalPages > 1) {
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2)
        const results = await Promise.all(
          remainingPages.map((page) => clientService.fetchClients({ limit: 100, page }))
        )
        for (const res of results) {
          allClients = allClients.concat(res.data)
        }
      }

      // Compute counts
      const responders = allClients.filter((c) => c.status === 'RESPONDED').length
      const nonResponders = allClients.filter(
        (c) => (c.customData as Record<string, unknown>)?.nonResponder === true
      ).length
      const newActive = allClients.filter((c) =>
        ['PENDING', 'EMAIL_1_SENT', 'EMAIL_2_SENT', 'EMAIL_3_SENT'].includes(c.status)
      ).length

      return {
        responders,
        nonResponders,
        newActive,
        inboxUnhandled: inboxStats?.unhandled ?? 0,
      }
    },
    refetchInterval: 60000,
  })
}
